<template>
  <div
    ref="root"
    class="md:flex md:justify-center"
    :class="[
      {
        'md:items-stretch': stretch,
        'md:items-center': !stretch,
        'md:flex-wrap': isSqueezedBySidebar,
      },
    ]"
  >
    <div
      v-if="!withoutLeftTemplate"
      class="max-w-full md:relative md:mr-32 md:min-w-[337px] md:max-w-[498px] md:grow lg:mr-64"
    >
      <div
        class="flex w-full flex-col justify-center opacity-100 transition-opacity duration-500 ease-out motion-reduce:transition-none"
        :class="[slotClasses.left]"
      >
        <slot name="left" />
      </div>
    </div>

    <div
      class="md:shrink-0"
      :class="{
        'md:w-[360px] min-[900px]:w-[456px]': !isCondensed,
        'w-full': isCondensed,
      }"
    >
      <div
        class="opacity-100 transition-opacity duration-500 ease-out motion-reduce:transition-none"
        :class="[slotClasses.right]"
      >
        <slot name="right" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onUnmounted, ref } from 'vue'

import type { Grade } from '@backmarket/http-api/src/api-models/Grade'
import type { I18nDefinition } from '@backmarket/nuxt-module-i18n/types/i18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { useIntersectionObserver } from '@vueuse/core'

import { useCondensedFunnelExperiment } from '~/scopes/product-core/components/presentationals/CustomizationFunnelCondensed/composables/useCondensedFunnelExperiment/useCondensedFunnelExperiment'
import { useShoppingAssistantSqueezeOffset } from '~/scopes/shopping-assistant/composables/useShoppingAssistantSqueezeOffset'

const { trackProductBlockImpression } = useTracking()
const root = ref<HTMLElement | null>()

// The right column has a fixed comfortable width (md:shrink-0) that a real
// viewport media query can't shrink out of the way for — so when the
// assistant squeeze eats into the available width, let the row wrap
// (illustration above, options below) instead of overflowing behind it.
const { squeezeOffsetPx } = useShoppingAssistantSqueezeOffset()
const isSqueezedBySidebar = computed(() => squeezeOffsetPx.value > 0)

const THRESHOLDS: Record<string, number> = {
  default: 0.5,
  recap: 0.25,
}

const props = withDefaults(
  defineProps<{
    slotClasses?: { left?: string; right?: string }
    tracking?: {
      trackingProducts?: Array<{
        product_id?: string
        product_price: string | undefined | null
        product_variant: Grade['value'] | undefined | null
        product_special_offer: number | undefined | null
        product_available: boolean
        product_option: string | I18nDefinition<string>
        campaign_id?: string | null
        population_id?: string | null
      }>
      trackingTags?: {
        tagDisplayed: string
        tagSelected: boolean
      }
      trackingId?: string
      trackingModel?: string
      trackingCategory?: string
    }
    stretch?: boolean
    withoutLeftTemplate?: boolean
  }>(),
  {
    slotClasses: () => ({
      left: '',
      right: '',
    }),
    tracking: () => ({}),
    stretch: false,
    withoutLeftTemplate: false,
  },
)

function trackImpression() {
  if (props.tracking.trackingId) {
    trackProductBlockImpression({
      products: props.tracking.trackingProducts ?? [],
      product_price_tag_displayed:
        props.tracking.trackingTags?.tagDisplayed ?? 'no_tag',
      product_price_tag_selected: Boolean(
        props.tracking.trackingTags?.tagSelected,
      ),
      block_name: props.tracking.trackingId,
      product_model: props.tracking.trackingModel || '',
      product_category: props.tracking.trackingCategory || '',
    })
  }
}

const { stop } = useIntersectionObserver(
  root,
  ([{ isIntersecting }]) => {
    if (isIntersecting) {
      trackImpression()
      stop()
    }
  },
  {
    threshold:
      props.tracking.trackingId && props.tracking.trackingId in THRESHOLDS
        ? THRESHOLDS[props.tracking.trackingId]
        : THRESHOLDS.default,
  },
)

const isCondensed = useCondensedFunnelExperiment()

onUnmounted(() => {
  stop()
})
</script>
